"use client";

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUploadStore } from "@shared/store/upload.store";
import { Card } from "@shared/ui/components/card";
import { Progress } from "@shared/ui/components/progress";
import { AlertCircle, CheckCircle2, FileAudio } from "lucide-react";

const AUTO_DISMISS_TIME = 7000; // 7 секунд

export const UploadProgress = (): JSX.Element | null => {
  const { activeUploads, setDismissed } = useUploadStore();
  const navigate = useNavigate();
  const [dismissTimers, setDismissTimers] = useState<Map<string, number>>(new Map());
  const startedTimersRef = useRef<Set<string>>(new Set());

  const uploads = Array.from(activeUploads.values()).filter(upload => !upload.dismissed);

  // Автоматическое скрытие завершенных загрузок через 7 секунд
  useEffect(() => {
    const completedUploads = uploads.filter(
      upload => upload.status === "completed" && !startedTimersRef.current.has(upload.uploadId)
    );

    completedUploads.forEach(upload => {
      startedTimersRef.current.add(upload.uploadId);

      // Устанавливаем начальное значение прогресса
      setDismissTimers(prev => new Map(prev).set(upload.uploadId, 100));

      // Обновляем прогресс каждые 50ms
      const interval = setInterval(() => {
        setDismissTimers(prev => {
          const current = prev.get(upload.uploadId) || 0;
          const newValue = Math.max(0, current - 100 / (AUTO_DISMISS_TIME / 50));
          const newMap = new Map(prev);
          newMap.set(upload.uploadId, newValue);
          return newMap;
        });
      }, 50);

      // Скрываем через 5 секунд (устанавливаем флаг dismissed в сторе!)
      setTimeout(() => {
        clearInterval(interval);
        setDismissed(upload.uploadId, true);
        startedTimersRef.current.delete(upload.uploadId);
        setDismissTimers(prev => {
          const newMap = new Map(prev);
          newMap.delete(upload.uploadId);
          return newMap;
        });
      }, AUTO_DISMISS_TIME);
    });
  }, [uploads, setDismissed]);

  if (uploads.length === 0) {
    return null;
  }

  const handleCardClick = (upload: any): void => {
    if (upload.status === "completed") {
      // Скрываем тост при клике
      setDismissed(upload.uploadId, true);
      // Переходим в историю
      navigate("/history");
    }
  };

  return (
    <div className="fixed bottom-2 left-4 right-4 sm:bottom-4 sm:left-4 sm:right-6 md:left-auto md:right-6 lg:right-8 z-50 space-y-2 max-w-[calc(100%-2rem)] sm:max-w-md w-auto md:w-full mx-auto md:mx-0">
      {uploads.map(upload => {
        const progress = Math.round((upload.uploadedChunks / upload.totalChunks) * 100);
        const isCompleted = upload.status === "completed";
        const dismissProgress = dismissTimers.get(upload.uploadId) || 0;

        return (
          <Card
            key={upload.uploadId}
            className={`p-3 sm:p-4 shadow-lg relative overflow-hidden ${isCompleted ? "cursor-pointer hover:shadow-xl transition-shadow" : ""}`}
            onClick={() => handleCardClick(upload)}
          >
            {/* Прогресс-бар исчезновения для завершенных загрузок */}
            {isCompleted && (
              <div
                className="absolute bottom-0 left-0 h-1 bg-green-500 transition-all duration-75"
                style={{ width: `${dismissProgress}%` }}
              />
            )}

            <div className="flex items-start gap-2 sm:gap-3">
              <div className="flex-shrink-0 mt-0.5 sm:mt-1">
                {upload.status === "completed" ? (
                  <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                ) : upload.status === "error" ? (
                  <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                ) : (
                  <FileAudio className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="overflow-hidden">
                  <p className="text-xs sm:text-sm font-medium mb-1 sm:mb-2 truncate">{upload.filename}</p>
                </div>

                {upload.status === "error" ? (
                  <p className="text-[10px] sm:text-xs text-red-500 leading-tight">{upload.errorMessage}</p>
                ) : upload.status === "completed" ? (
                  <p className="text-[10px] sm:text-xs text-green-500 leading-tight">
                    Загрузка завершена • Нажмите для перехода в историю
                  </p>
                ) : (
                  <>
                    <Progress value={progress} className="h-1.5 sm:h-2 mb-1" />
                    <div className="flex justify-between text-[10px] sm:text-xs text-gray-500">
                      <span>{/* {upload.uploadedChunks} / {upload.totalChunks} частей */}</span>
                      <span>{progress}%</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};
